import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Shield, Users, Brain, CreditCard, AlertTriangle, CheckCircle, RefreshCw, ArrowLeft, Trash2, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('admin_key') || '');
  const [passcode, setPasscode] = useState('');
  const [checkingKey, setCheckingKey] = useState(false);

  const fetchData = useCallback(async (customKey) => {
    const keyToUse = customKey || adminKey;
    if (!keyToUse) return;
    setLoading(true);
    setError('');
    try {
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { 'X-Admin-Key': keyToUse } }),
        axios.get(`${API}/admin/security-logs?limit=100${filter !== 'all' ? `&severity=${filter}` : ''}`, { headers: { 'X-Admin-Key': keyToUse } })
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data. Check your admin key.');
      if (err.response?.status === 403 || err.response?.status === 401) {
        setAdminKey('');
        sessionStorage.removeItem('admin_key');
      }
    }
    setLoading(false);
  }, [filter, adminKey]);

  useEffect(() => {
    if (adminKey) {
      fetchData();
    }
  }, [fetchData, adminKey]);

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setCheckingKey(true);
    setError('');
    try {
      const statsRes = await axios.get(`${API}/admin/stats`, {
        headers: { 'X-Admin-Key': passcode.trim() }
      });
      setAdminKey(passcode.trim());
      sessionStorage.setItem('admin_key', passcode.trim());
      setStats(statsRes.data);
      const eventsRes = await axios.get(`${API}/admin/security-logs?limit=100`, {
        headers: { 'X-Admin-Key': passcode.trim() }
      });
      setEvents(eventsRes.data.events || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid Admin Passcode');
    } finally {
      setCheckingKey(false);
    }
  };

  const acknowledgeEvent = async (eventId) => {
    try {
      await axios.post(`${API}/admin/security-logs/${eventId}/acknowledge`, {}, { headers: { 'X-Admin-Key': adminKey } });
      fetchData();
    } catch { }
  };

  const clearOldEvents = async () => {
    if (!window.confirm('Delete security events older than 30 days?')) return;
    try {
      const res = await axios.delete(`${API}/admin/security-logs/clear?days=30`, { headers: { 'X-Admin-Key': adminKey } });
      alert(`Deleted ${res.data.deleted} old events`);
      fetchData();
    } catch { }
  };

  const severityColor = (s) => ({
    low: '#3B82F6', medium: '#F59E0B', high: '#EF4444', critical: '#DC2626'
  }[s] || '#888');

  const severityBg = (s) => ({
    low: 'rgba(59,130,246,0.1)', medium: 'rgba(245,158,11,0.1)', high: 'rgba(239,68,68,0.1)', critical: 'rgba(220,38,38,0.15)'
  }[s] || 'rgba(136,136,136,0.1)');

  // ADMIN CHECK — after all hooks
  const ADMIN_EMAILS = ['ritik@solvailabs.com'];
  if (user && !ADMIN_EMAILS.includes(user.email)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <h2 className="text-xl font-bold text-white mb-2">Not Authorized</h2>
          <p className="text-sm" style={{ color: '#94A3B8' }}>This page is restricted.</p>
        </div>
      </div>
    );
  }

  if (!adminKey) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#050505' }}>
        <div className="w-full max-w-md glass-card rounded-2xl p-8 border border-white/5 glow-primary animate-fadeInUp">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
              <Shield className="w-6 h-6" style={{ color: '#6366F1' }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Syne' }}>Admin Verification</h2>
            <p className="text-sm" style={{ color: '#94A3B8' }}>Enter your administrator passcode to access the security console.</p>
          </div>

          <form onSubmit={handlePasscodeSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
                Admin Passcode
              </label>
              <input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="••••••••••••••••"
                className="w-full px-4 py-3 rounded-xl border bg-black/50 text-white placeholder-slate-600 focus:outline-none transition-all"
                style={{
                  borderColor: '#1f1f2e',
                  fontFamily: 'monospace'
                }}
                disabled={checkingKey}
                autoFocus
              />
            </div>

            {error && (
              <p className="text-sm font-medium text-center" style={{ color: '#EF4444' }}>
                {error}
              </p>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 rounded-xl py-3 border hover:bg-white/5 transition-all text-sm font-medium"
                style={{
                  background: 'transparent',
                  borderColor: '#1f1f2e',
                  color: '#94A3B8'
                }}
                disabled={checkingKey}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-xl py-3 text-white text-sm font-medium hover:-translate-y-0.5 transition-all"
                style={{
                  background: '#6366F1'
                }}
                disabled={checkingKey}
              >
                {checkingKey ? 'Verifying...' : 'Access Console'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full px-6" style={{ background: '#6366F1' }}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#050505', color: '#fff' }}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 rounded-lg hover:bg-white/5">
              <ArrowLeft className="w-5 h-5" style={{ color: '#94A3B8' }} />
            </button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Syne' }}>Admin Dashboard</h1>
              <p className="text-sm" style={{ color: '#64748B' }}>Security monitoring & analytics</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} className="rounded-lg px-4 py-2 text-sm" style={{ background: '#1e1e2e', border: '1px solid #333' }}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button onClick={clearOldEvents} className="rounded-lg px-4 py-2 text-sm" style={{ background: '#1e1e2e', border: '1px solid #333' }}>
              <Trash2 className="w-4 h-4 mr-2" /> Clear Old
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={<Users className="w-5 h-5" />} label="Total Users" value={stats.overview.total_users} sub={`+${stats.overview.new_users_24h} today`} color="#6366F1" />
            <StatCard icon={<Brain className="w-5 h-5" />} label="Simulations" value={stats.overview.total_simulations} sub={`${stats.overview.total_decisions} decisions`} color="#8B5CF6" />
            <StatCard icon={<CreditCard className="w-5 h-5" />} label="Paid Transactions" value={stats.overview.total_paid_transactions} sub="all time" color="#10B981" />
            <StatCard icon={<AlertTriangle className="w-5 h-5" />} label="Threats (24h)" value={stats.security.events_24h} sub={`${stats.security.high_severity_24h} high severity`} color={stats.security.high_severity_24h > 0 ? '#EF4444' : '#F59E0B'} />
          </div>
        )}

        <div className="rounded-xl p-6" style={{ background: '#0a0a1a', border: '1px solid #1a1a2e' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Syne' }}>Security Events</h2>
            <div className="flex gap-2">
              {['all', 'critical', 'high', 'medium', 'low'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="px-3 py-1 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: filter === f ? (f === 'all' ? '#6366F1' : severityColor(f)) : '#1e1e2e',
                    color: filter === f ? '#fff' : '#888',
                    border: `1px solid ${filter === f ? 'transparent' : '#333'}`
                  }}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {events.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="w-12 h-12 mx-auto mb-3" style={{ color: '#10B981' }} />
              <p className="text-sm" style={{ color: '#64748B' }}>No security events. All clear!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((event) => (
                <div
                  key={event.event_id}
                  className="flex items-center justify-between p-3 rounded-lg transition-all"
                  style={{
                    background: severityBg(event.severity),
                    borderLeft: `3px solid ${severityColor(event.severity)}`,
                    opacity: event.acknowledged ? 0.5 : 1
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: severityColor(event.severity), color: '#fff' }}>
                        {event.severity?.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-white truncate">{event.event_type}</span>
                      {event.acknowledged && <CheckCircle className="w-3 h-3" style={{ color: '#10B981' }} />}
                    </div>
                    <p className="text-xs truncate" style={{ color: '#94A3B8' }}>{event.details}</p>
                    <div className="flex gap-4 mt-1">
                      <span className="text-xs" style={{ color: '#64748B' }}>IP: {event.ip_address}</span>
                      <span className="text-xs" style={{ color: '#64748B' }}>
                        {event.timestamp ? new Date(event.timestamp).toLocaleString() : '\u2014'}
                      </span>
                    </div>
                  </div>
                  {!event.acknowledged && (
                    <button
                      onClick={() => acknowledgeEvent(event.event_id)}
                      className="ml-3 p-2 rounded-lg hover:bg-white/10 transition-all"
                      title="Acknowledge"
                    >
                      <Eye className="w-4 h-4" style={{ color: '#64748B' }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div className="rounded-xl p-5" style={{ background: '#0a0a1a', border: '1px solid #1a1a2e' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg" style={{ background: `${color}20`, color }}>{icon}</div>
        <span className="text-xs font-medium" style={{ color: '#64748B' }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ fontFamily: 'Syne' }}>{value}</p>
      <p className="text-xs mt-1" style={{ color: '#64748B' }}>{sub}</p>
    </div>
  );
}